/*
 * View More pagination for collection grids.
 *
 * - Upgrades the real next-page anchor rendered by snippets/load-more.liquid.
 * - Fetches the next page through the Section Rendering API (?section_id=) with
 *   every active filter and sort parameter carried on the URL.
 * - Appends product tiles in place, updates the counter, moves focus to the first
 *   new tile, and rewrites the URL to ?page=N with history.replaceState.
 * - Persists loaded depth and scroll position in sessionStorage so returning from
 *   a product page restores the shopper's place.
 * - A filter or sort change re-renders [data-products-grid], which replaces this
 *   element with a fresh batch-1 instance.
 */
(function () {
  'use strict';

  const SELECTORS = {
    gridOuter: '[data-products-grid]',
    grid: '.grid',
    item: '.grid-item.product-item',
    button: '[data-load-more-button]',
    prev: '[data-load-more-prev]',
    label: '[data-load-more-label]',
    count: '[data-load-more-count]',
    error: '[data-load-more-error]',
    retry: '[data-load-more-retry]',
    end: '[data-load-more-end]',
  };

  const STORAGE_PREFIX = 'load-more:';
  const MAX_RESTORE_PAGES = 10;

  let restoredThisPageLoad = false;

  function navigationType() {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries && entries.length) return entries[0].type;
    } catch (e) {
      /* noop */
    }
    return 'navigate';
  }

  function storage(action, key, value) {
    try {
      if (action === 'get') {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      }
      if (action === 'set') sessionStorage.setItem(key, JSON.stringify(value));
      if (action === 'remove') sessionStorage.removeItem(key);
    } catch (e) {
      /* storage unavailable; feature degrades gracefully */
    }
    return null;
  }

  class LoadMore extends HTMLElement {
    constructor() {
      super();
      this.onButtonClick = this.onButtonClick.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onRetryClick = this.onRetryClick.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
    }

    connectedCallback() {
      this.gridOuter = this.closest(SELECTORS.gridOuter);
      this.grid = this.gridOuter ? this.gridOuter.querySelector(SELECTORS.grid) : null;
      this.button = this.querySelector(SELECTORS.button);
      // "View previous" is rendered above the grid (position: 'top'), outside this element.
      this.prev = this.gridOuter ? this.gridOuter.querySelector(SELECTORS.prev) : null;
      this.count = this.querySelector(SELECTORS.count);
      this.error = this.querySelector(SELECTORS.error);
      this.retry = this.querySelector(SELECTORS.retry);
      this.end = this.querySelector(SELECTORS.end);

      this.sectionId = this.dataset.sectionId;
      this.collection = this.dataset.collection || '';
      this.page = parseInt(this.dataset.page, 10) || 1;
      this.pages = parseInt(this.dataset.pages, 10) || 1;
      this.perPage = parseInt(this.dataset.perPage, 10) || 48;
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.shown = parseInt(this.dataset.shown, 10) || 0;
      this.first = parseInt(this.dataset.first, 10) || 1;
      this.loading = false;
      this.loadingPrev = false;

      if (!this.grid) return;

      if (this.button) this.button.addEventListener('click', this.onButtonClick);
      if (this.prev) this.prev.addEventListener('click', this.onPrevClick);
      if (this.retry) this.retry.addEventListener('click', this.onRetryClick);
      this.grid.addEventListener('click', this.onGridClick);

      this.restore();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      if (this.prev) this.prev.removeEventListener('click', this.onPrevClick);
      if (this.retry) this.retry.removeEventListener('click', this.onRetryClick);
      if (this.grid) this.grid.removeEventListener('click', this.onGridClick);
    }

    /* ---------- state helpers ---------- */

    // Query string without the page parameter: the identity of this filtered/sorted view.
    viewSearch() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      const s = params.toString();
      return s ? `?${s}` : '';
    }

    storageKey() {
      return `${STORAGE_PREFIX}${window.location.pathname}${this.viewSearch()}`;
    }

    text(name, replacements) {
      let str = this.dataset[name] || '';
      Object.keys(replacements || {}).forEach((key) => {
        str = str.replace(`[${key}]`, replacements[key]);
      });
      return str;
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (!this.button) return;
      const label = this.button.querySelector(SELECTORS.label);
      this.button.classList.toggle('is-loading', isLoading);
      this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      if (label) label.textContent = isLoading ? this.text('textLoading') : this.text('textViewMore');
    }

    updateCounter() {
      if (this.count) {
        // From page 1: "Showing 48 of 754". From a deep link: "Showing 25–72 of 754".
        // Prepending back to page 1 drops the range form again.
        this.count.textContent = this.first > 1
          ? this.text('textShowingRange', { first: this.first, last: this.shown, total: this.total })
          : this.text('textShowing', { shown: this.shown, total: this.total });
      }
      if (this.button) {
        const remaining = Math.max(0, this.total - this.shown);
        this.button.setAttribute('aria-label', this.text('textViewMoreAria', { count: Math.min(remaining, this.perPage) }));
      }
    }

    updateUrl() {
      const url = new URL(window.location.href);
      if (this.page > 1) url.searchParams.set('page', String(this.page));
      else url.searchParams.delete('page');
      window.history.replaceState(window.history.state, '', url.toString());
    }

    finish() {
      if (this.button) {
        this.button.removeEventListener('click', this.onButtonClick);
        this.button.remove();
        this.button = null;
      }
      if (this.end) {
        this.end.textContent = this.text('textEnd');
        this.end.hidden = false;
      }
    }

    /* ---------- events ---------- */

    onButtonClick(event) {
      event.preventDefault();
      if (this.loading) return;
      this.load().then((appended) => {
        if (appended) this.track();
        this.persist();
      });
    }

    onRetryClick(event) {
      event.preventDefault();
      this.onButtonClick(event);
    }

    onPrevClick(event) {
      event.preventDefault();
      if (this.loadingPrev) return;
      this.loadPrevious();
    }

    // Fetches the page before the earliest loaded one and prepends its tiles,
    // keeping whatever the shopper was looking at in the same place on screen.
    loadPrevious() {
      if (!this.prev) return Promise.resolve(0);

      const requestUrl = new URL(this.prev.href, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);
      const label = this.prev.querySelector(SELECTORS.label);

      this.loadingPrev = true;
      this.prev.classList.add('is-loading');
      this.prev.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = this.text('textLoading');
      this.grid.setAttribute('aria-busy', 'true');

      return fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(`View previous request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incomingOuter = doc.querySelector(SELECTORS.gridOuter) || doc;
          const items = incomingOuter.querySelectorAll(SELECTORS.item);
          const incomingPrev = incomingOuter.querySelector(SELECTORS.prev);

          const fragment = document.createDocumentFragment();
          const existingIds = new Set(Array.from(this.grid.querySelectorAll(SELECTORS.item)).map((el) => el.id));
          let first = null;
          items.forEach((item) => {
            if (item.id && existingIds.has(item.id)) return;
            if (!first) first = item;
            fragment.appendChild(item);
          });
          const prepended = fragment.childElementCount;

          const heightBefore = document.documentElement.scrollHeight;
          this.grid.insertBefore(fragment, this.grid.firstChild);
          const heightAfter = document.documentElement.scrollHeight;
          window.scrollBy(0, heightAfter - heightBefore);

          const loadedPage = parseInt(requestUrl.searchParams.get('page'), 10) || 1;
          this.first = (loadedPage - 1) * this.perPage + 1;

          if (incomingPrev && loadedPage > 1) {
            this.prev.href = new URL(incomingPrev.getAttribute('href'), window.location.origin).toString();
            this.prev.classList.remove('is-loading');
            this.prev.setAttribute('aria-disabled', 'false');
            if (label) label.textContent = this.text('textViewPrevious');
          } else {
            this.prev.removeEventListener('click', this.onPrevClick);
            this.prev.closest('.load-more--top').remove();
            this.prev = null;
          }

          this.updateCounter();
          this.loadingPrev = false;
          this.grid.setAttribute('aria-busy', 'false');

          if (first) {
            first.setAttribute('tabindex', '-1');
            first.focus({ preventScroll: true });
          }

          this.dispatchEvent(new CustomEvent('theme:load-more:prepended', { bubbles: true, detail: { page: loadedPage, prepended } }));
          return prepended;
        })
        .catch((err) => {
          console.warn(err);
          this.loadingPrev = false;
          this.grid.setAttribute('aria-busy', 'false');
          if (this.prev) {
            this.prev.classList.remove('is-loading');
            this.prev.setAttribute('aria-disabled', 'false');
            if (label) label.textContent = this.text('textViewPrevious');
          }
          if (this.error) this.error.hidden = false;
          return 0;
        });
    }

    onGridClick(event) {
      const item = event.target.closest(SELECTORS.item);
      if (!item) return;
      this.persist(item);
    }

    /* ---------- fetching ---------- */

    // Fetches the next page and appends its tiles. Resolves with the number of tiles appended.
    load() {
      if (!this.button) return Promise.resolve(0);

      const requestUrl = new URL(this.button.href, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      this.setLoading(true);
      if (this.error) this.error.hidden = true;

      return fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(`View More request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incomingOuter = doc.querySelector(SELECTORS.gridOuter) || doc;
          const items = incomingOuter.querySelectorAll(SELECTORS.item);
          const incoming = doc.querySelector('[data-load-more]');
          const incomingButton = incoming ? incoming.querySelector(SELECTORS.button) : null;

          const fragment = document.createDocumentFragment();
          const existingIds = new Set(Array.from(this.grid.querySelectorAll(SELECTORS.item)).map((el) => el.id));
          let first = null;
          items.forEach((item) => {
            if (item.id && existingIds.has(item.id)) return;
            if (!first) first = item;
            fragment.appendChild(item);
          });
          const appended = fragment.childElementCount;
          this.grid.appendChild(fragment);

          this.page = parseInt(requestUrl.searchParams.get('page'), 10) || this.page + 1;
          this.shown = incoming && incoming.dataset.shown ? parseInt(incoming.dataset.shown, 10) : this.shown + appended;
          if (incoming && incoming.dataset.total) this.total = parseInt(incoming.dataset.total, 10);

          if (incomingButton) {
            this.button.href = new URL(incomingButton.getAttribute('href'), window.location.origin).toString();
          } else {
            this.finish();
          }

          this.updateCounter();
          this.updateUrl();
          this.setLoading(false);

          if (first) {
            first.setAttribute('tabindex', '-1');
            first.focus({ preventScroll: true });
          }

          this.dispatchEvent(new CustomEvent('theme:load-more:appended', { bubbles: true, detail: { page: this.page, appended } }));
          return appended;
        })
        .catch((err) => {
          console.warn(err);
          this.setLoading(false);
          if (this.error) {
            this.error.hidden = false;
            if (this.retry) this.retry.focus();
          }
          return 0;
        });
    }

    /* ---------- restore on back navigation ---------- */

    persist(item) {
      storage('set', this.storageKey(), {
        page: this.page,
        scrollY: window.scrollY,
        itemId: item ? item.id : null,
        ts: Date.now(),
      });
    }

    restore() {
      if (restoredThisPageLoad) return;
      restoredThisPageLoad = true;

      const key = this.storageKey();
      const state = storage('get', key);
      const type = navigationType();

      if (!state || type !== 'back_forward') {
        if (state && type !== 'back_forward') storage('remove', key);
        return;
      }

      const targetPage = Math.min(state.page || 1, this.pages, this.page + MAX_RESTORE_PAGES);
      const loadUntil = () => {
        if (this.page >= targetPage || !this.button) return Promise.resolve();
        return this.load().then((appended) => (appended ? loadUntil() : Promise.resolve()));
      };

      loadUntil().then(() => {
        const target = state.itemId ? document.getElementById(state.itemId) : null;
        if (target) {
          target.scrollIntoView({ block: 'center' });
        } else if (typeof state.scrollY === 'number') {
          window.scrollTo(0, state.scrollY);
        }
      });
    }

    /* ---------- analytics ---------- */

    track() {
      const payload = {
        event: 'view_more_clicked',
        collection: this.collection,
        batch: this.page,
        products_loaded: this.shown,
        filters: this.viewSearch(),
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_more_clicked', {
          collection: payload.collection,
          batch: payload.batch,
          products_loaded: payload.products_loaded,
          filters: payload.filters,
        });
      }
    }
  }

  if (!customElements.get('load-more')) customElements.define('load-more', LoadMore);
})();
